import { Injectable } from '@angular/core';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { LocalStorageService } from '@library/utils/services/local-storage.service';
import { CpCompareSidebarCardsService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar-cards.service';
import { firstValueFrom } from 'rxjs';
import { MeasureCard } from './cp-compare-sidebar.service';

const COMPARE_SIDEBAR_CACHE_KEY = 'compare-sidebar-cache-v2';

export interface SidebarCacheCompareDataset {
  homeId: string,
  moduleId?: string,
  roomId?: string,
  measureType: EnumMeasureTypes,
}

interface SidebarCache {
  datasets: Record<string, SidebarCacheCompareDataset[]>
}

@Injectable()
export class CPCompareSidebarCacheService {

  private cache: SidebarCache = JSON.parse(this.localStorage.getItem(COMPARE_SIDEBAR_CACHE_KEY) ?? '{}');

  datasets = this.cache.datasets ?? {};

  constructor(
    public localStorage: LocalStorageService,
    private homesFacade: HomesFacade,
    private compareSidebarCardsService: CpCompareSidebarCardsService,
  ) {}

  async setDataset(measureCard: MeasureCard) {
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);
    const dataset: SidebarCacheCompareDataset = {
      homeId: homeId,
      moduleId: measureCard.module?.id,
      roomId: measureCard.room?.id,
      measureType: measureCard.measureType,
    };

    // add dataset only if not already existing
    if (this.datasets[dataset.homeId] &&
       !this.datasets[dataset.homeId].find(homeDataset => this.isCardInDataset(homeDataset, dataset))) {
      this.datasets[dataset.homeId].push(dataset);
    }
    else if (!this.datasets[dataset.homeId]) {
      this.datasets[dataset.homeId] = [dataset];
    }
    this.save();
  }

  async removeDataset(measureCard: MeasureCard) {
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);
    const dataset = {
      homeId: homeId,
      moduleId: measureCard.module?.id,
      roomId: measureCard.room?.id,
      measureType: measureCard.measureType,
    };

    this.datasets[dataset.homeId] = (this.datasets[dataset.homeId] ?? []).filter(homeDataset =>
      !this.isCardInDataset(homeDataset, dataset)
    );
    this.save();
  }

  async getCacheMeasureCards() {
    const currentHomeId = await firstValueFrom(this.homesFacade.currentHomeId$.pipe(isDefined()));

    const cacheMeasureCards = this.datasets[currentHomeId] ?? [];

    const measureCards = await firstValueFrom(this.compareSidebarCardsService.measureCards$);

    return cacheMeasureCards.map(cacheMeasureCard => {
      const {homeId, moduleId, roomId, measureType} = cacheMeasureCard;

      if (roomId) {
        return measureCards.find(measureCard => measureCard.room?.id === roomId && measureCard.measureType === measureType);
      }
      else if (moduleId) {
        return measureCards.find(measureCard => measureCard.module?.id === moduleId && measureCard.measureType === measureType);
      }
      else if (homeId) {
        return measureCards.find(measureCard => measureCard.home?.id === homeId && measureCard.measureType === measureType);
      }
    }).filter(card => !!card);
  }

  save() {
    this.localStorage.setItem(COMPARE_SIDEBAR_CACHE_KEY, JSON.stringify({datasets: this.datasets}));
  }

  isCardInDataset(homeDataset: SidebarCacheCompareDataset, dataset: SidebarCacheCompareDataset) {
    return homeDataset.measureType === dataset.measureType
      && (!homeDataset.moduleId || homeDataset.moduleId === dataset.moduleId)
      && (!homeDataset.roomId || homeDataset.roomId === dataset.roomId)
      && (!homeDataset.homeId || homeDataset.homeId === dataset.homeId);
  }
}
